/**
 * Place Details Container Component
 * Container component with business logic for place details
 * Handles API calls, data binding, and state management
 */

import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  PlaceDetailsPresenter,
  PlaceDetailsPresenterProps,
} from './PlaceDetailsPresenter';
import { useTravelAssistantOverview } from '@/hooks/useTravelAssistantOverview';
import { useQuickPeek } from '@/hooks/useQuickPeek';
import { generateUserId } from '@/services/chatService';
import { ChatMessage, ChatLocation } from '@/types/chatTypes';
import type { QuickPeekResponse } from '@/services/api/interfaces/ITravelAssistantRepository';
import {
  useOverViewStore,
  useOverviewImagesStore,
  useQuickPeekStore,
} from '@/store/overViewStore';
import { imageService, ThumbnailImageProvider } from '@/services/imageService';

export interface PlaceDetailsContainerProps {
  messages: ChatMessage[];
  sessionId: string | null;
  initialSelectedLocation?: ChatLocation | null;
  onBack?: () => void;
}

export const PlaceDetailsContainer: React.FC<PlaceDetailsContainerProps> = ({
  messages,
  sessionId,
  initialSelectedLocation,
  onBack,
}) => {
  // Manage selected location state internally
  const [selectedLocation, setSelectedLocation] = useState<ChatLocation | null>(
    initialSelectedLocation || null
  );

  // Update selected location when prop changes
  useEffect(() => {
    console.log(
      '🔵 [PlaceDetailsContainer] initialSelectedLocation prop changed:',
      {
        title: initialSelectedLocation?.title,
        place: initialSelectedLocation?.place,
        place_name: initialSelectedLocation?.place_name,
        place_id: initialSelectedLocation?.place_id,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
      }
    );
    setSelectedLocation(initialSelectedLocation || null);
  }, [initialSelectedLocation]);

  // Handle back button - clear both internal state and notify parent
  const handleBack = () => {
    setSelectedLocation(null);
    // Notify parent to clear selectedLocation so the right side is removed from DOM
    onBack?.();
  };
  // Get user ID for overview API
  const userId = useMemo(() => {
    try {
      return generateUserId();
    } catch (err) {
      console.error('Error generating user ID:', err);
      return 'string'; // Fallback to default
    }
  }, []);

  // Extract location from messages for overview API
  const extractedLocation = useMemo(() => {
    // If a location is explicitly selected, use that
    if (selectedLocation) {
      // Prioritize place_name since that's what we explicitly set when a place name is clicked
      // This ensures every click on a place name triggers a new API call
      const extracted =
        selectedLocation.place_name ||
        selectedLocation.title ||
        selectedLocation.place ||
        selectedLocation.locality ||
        '';
      console.log('🟣 [PlaceDetailsContainer] extractedLocation computed:', {
        extracted,
        title: selectedLocation.title,
        place: selectedLocation.place,
        place_name: selectedLocation.place_name,
        locality: selectedLocation.locality,
        timestamp: new Date().toISOString(),
      });
      return extracted;
    }

    // Try to get location from the latest assistant message's locations
    const latestMessage = messages
      .slice()
      .reverse()
      .find(
        msg =>
          msg.role === 'assistant' && msg.locations && msg.locations.length > 0
      );

    if (latestMessage?.locations && latestMessage.locations.length > 0) {
      const firstLocation = latestMessage.locations[0];
      return (
        firstLocation.title ||
        firstLocation.place ||
        firstLocation.place_name ||
        firstLocation.locality ||
        ''
      );
    }

    // Try to extract from user's last query
    const lastUserMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.role === 'user');

    if (lastUserMessage?.content) {
      // Simple extraction - could be enhanced with NLP
      return lastUserMessage.content.trim();
    }

    return '';
  }, [messages, selectedLocation]);

  // Store hooks for updating overview data
  const {
    addOverview,
    addFestival,
    addPlaceOfInterest,
    clearOverViewData,
    updateFestivalImages,
  } = useOverViewStore();
  const { clearImages } = useOverviewImagesStore();
  const {
    clearQuickPeekData,
    addInfoPointer,
    addModeOfTransport,
    addTravelPlanning,
    setQuickPeekResponse,
    setLoadingActivitiesFacilities,
    setLoadingTravelPlanningOnly,
    setLoadingNavigationAccessibility,
    setLoadingCulturalSocial,
    setLoadingHistoricalSouvenirs,
  } = useQuickPeekStore();

  // Use the overview hook (with cache for instant display on reopen)
  const {
    isLoading: isOverviewLoading,
    fetchOverview: fetchOverviewData,
    getCachedOverview,
  } = useTravelAssistantOverview();

  // Use the quick peek hook with separate API calls (with cache for instant Travel Guide on reopen)
  const {
    fetchTravelPlanning,
    fetchActivitiesFacilities,
    fetchTravelPlanningParallel,
    fetchCulturalParallel,
    isLoadingTravelPlanning,
    isLoadingActivitiesFacilities,
    getCachedQuickPeek,
    setCachedQuickPeek, // Add this to cache complete merged response
  } = useQuickPeek();

  // Combined loading state for all quick peek APIs
  // Note: Individual loading states are managed separately for each of the 5 APIs
  const isQuickPeekLoadingCombined =
    isLoadingTravelPlanning || isLoadingActivitiesFacilities;

  // Track last fetched place name to trigger API calls on every click
  // Use place name (not location key) so every click triggers a new API call
  const lastFetchedPlaceNameRef = useRef<string>('');
  // Track last fetched place_id for reference (not used for validation - name takes priority)
  const lastFetchedPlaceIdRef = useRef<string>('');
  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef<boolean>(false);
  // Track the current request ID to cancel stale responses
  // Using a counter ensures each request has a unique ID
  const currentRequestIdRef = useRef<number>(0);
  // Track the current location name to validate responses
  // This ensures we reject responses even if requestId matches (due to component remount)
  const currentLocationNameRef = useRef<string>('');
  // AbortController to cancel pending requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Store function references in refs to avoid dependency issues
  const fetchOverviewDataRef = useRef(fetchOverviewData);
  const fetchTravelPlanningRef = useRef(fetchTravelPlanning);
  const fetchTravelPlanningParallelRef = useRef(fetchTravelPlanningParallel);
  const fetchCulturalParallelRef = useRef(fetchCulturalParallel);
  const fetchActivitiesFacilitiesRef = useRef(fetchActivitiesFacilities);

  // Update refs when functions change (they should be stable from React Query)
  useEffect(() => {
    const overviewChanged = fetchOverviewDataRef.current !== fetchOverviewData;
    const travelPlanningChanged =
      fetchTravelPlanningRef.current !== fetchTravelPlanning;
    const travelPlanningParallelChanged =
      fetchTravelPlanningParallelRef.current !== fetchTravelPlanningParallel;
    const culturalParallelChanged =
      fetchCulturalParallelRef.current !== fetchCulturalParallel;
    const activitiesFacilitiesChanged =
      fetchActivitiesFacilitiesRef.current !== fetchActivitiesFacilities;
    if (
      overviewChanged ||
      travelPlanningChanged ||
      travelPlanningParallelChanged ||
      culturalParallelChanged ||
      activitiesFacilitiesChanged
    ) {
      console.log('🟡 [PlaceDetailsContainer] Function references changed:', {
        overviewChanged,
        travelPlanningChanged,
        travelPlanningParallelChanged,
        culturalParallelChanged,
        activitiesFacilitiesChanged,
        timestamp: new Date().toISOString(),
      });
    }
    fetchOverviewDataRef.current = fetchOverviewData;
    fetchTravelPlanningRef.current = fetchTravelPlanning;
    fetchTravelPlanningParallelRef.current = fetchTravelPlanningParallel;
    fetchCulturalParallelRef.current = fetchCulturalParallel;
    fetchActivitiesFacilitiesRef.current = fetchActivitiesFacilities;
  }, [
    fetchOverviewData,
    fetchTravelPlanning,
    fetchTravelPlanningParallel,
    fetchCulturalParallel,
    fetchActivitiesFacilities,
  ]);

  // Reset ref and clear data when selectedLocation changes
  // This ensures that when a new location is selected, we always fetch new data
  useEffect(() => {
    // Use the same extraction logic as extractedLocation to ensure consistency
    const currentPlaceName = selectedLocation
      ? selectedLocation.place_name ||
        selectedLocation.title ||
        selectedLocation.place ||
        selectedLocation.locality ||
        ''
      : '';

    console.log(
      '🟢 [PlaceDetailsContainer] selectedLocation state changed effect triggered:',
      {
        hasLocation: !!selectedLocation,
        title: selectedLocation?.title,
        place: selectedLocation?.place,
        place_name: selectedLocation?.place_name,
        place_id: selectedLocation?.place_id,
        currentPlaceName,
        extractedLocation,
        previousLastFetched: lastFetchedPlaceNameRef.current,
        previousIsFetching: isFetchingRef.current,
        previousRequestId: currentRequestIdRef.current,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
      }
    );

    if (!selectedLocation) {
      lastFetchedPlaceNameRef.current = '';
      lastFetchedPlaceIdRef.current = '';
      isFetchingRef.current = false;
      // Increment request ID to invalidate all pending requests
      const oldRequestId = currentRequestIdRef.current;
      currentRequestIdRef.current += 1;
      currentLocationNameRef.current = '';

      // Abort any pending requests when location is cleared
      if (abortControllerRef.current) {
        console.log(
          '🟢 [PlaceDetailsContainer] 🛑 Location cleared, aborting pending requests'
        );
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      console.log(
        '🟢 [PlaceDetailsContainer] Selected location cleared, resetting refs',
        {
          oldRequestId,
          newRequestId: currentRequestIdRef.current,
          timestamp: new Date().toISOString(),
        }
      );
      // Clear data when location is cleared
      console.log(
        '🟢 [PlaceDetailsContainer] Clearing all overview/quickpeek data...'
      );
      clearOverViewData();
      clearImages();
      clearQuickPeekData();
      console.log('🟢 [PlaceDetailsContainer] ✅ All data cleared', {
        timestamp: new Date().toISOString(),
      });
    } else {
      // Only reset refs if this is a DIFFERENT location
      // This prevents duplicate API calls when React re-renders and sets the same location multiple times
      // Use extractedLocation (place name) for comparison - if name is different, treat as different location
      // This ensures clicks on places with different names always trigger updates, regardless of place_id
      const currentPlaceId = selectedLocation?.place_id || '';
      const nameDifferent =
        extractedLocation !== lastFetchedPlaceNameRef.current &&
        extractedLocation !== '';
      // Treat as different if name is different (name takes priority over place_id)
      const isDifferentLocation = nameDifferent;
      const wasFetching = isFetchingRef.current;

      console.log('🟢 [PlaceDetailsContainer] Location comparison:', {
        extractedLocation,
        lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
        currentPlaceId,
        lastFetchedPlaceId: lastFetchedPlaceIdRef.current,
        nameMatches: extractedLocation === lastFetchedPlaceNameRef.current,
        idMatches: currentPlaceId === lastFetchedPlaceIdRef.current,
        isDifferentLocation,
        timestamp: new Date().toISOString(),
      });

      if (isDifferentLocation) {
        const previousLastFetched = lastFetchedPlaceNameRef.current;
        const previousRequestId = currentRequestIdRef.current;

        // CRITICAL: Increment request ID IMMEDIATELY to cancel any pending requests
        // This ensures that any responses from previous requests will be ignored
        console.log(
          '🟢 [PlaceDetailsContainer] ⚠️ CANCELLING pending requests by incrementing request ID',
          {
            previousRequestId,
            previousLocation: previousLastFetched,
            newLocation: extractedLocation,
            timestamp: new Date().toISOString(),
          }
        );

        // Abort previous requests using AbortController
        if (abortControllerRef.current) {
          console.log(
            '🟢 [PlaceDetailsContainer] 🛑 Aborting previous AbortController'
          );
          abortControllerRef.current.abort();
        }
        // Create new controller for the new location
        abortControllerRef.current = new AbortController();

        currentRequestIdRef.current += 1;
        const newRequestId = currentRequestIdRef.current;
        // CRITICAL: Update current location name ref immediately with extractedLocation
        // This ensures location name validation works even if component remounts
        // Use extractedLocation (not currentPlaceName) to match what we use for API calls
        currentLocationNameRef.current = extractedLocation;

        // Reset lastFetched to allow new API call for different location
        // BUT: Don't reset if we're currently fetching the same location
        // This prevents re-renders from thinking it's a different location
        // We'll set these refs when the API call actually starts
        // Reset lastFetched to allow new API call for different location
        // We know it's different because isDifferentLocation is true
        lastFetchedPlaceNameRef.current = '';
        lastFetchedPlaceIdRef.current = '';

        // CRITICAL: Always reset isFetching when location changes
        // This ensures checking !isFetchingRef.current in the next effect will pass
        // allowing the new location to be fetched immediately
        isFetchingRef.current = false;
        console.log(
          '🟢 [PlaceDetailsContainer] Location changed, resetting refs and cancelling pending requests:',
          {
            title: selectedLocation.title,
            place: selectedLocation.place,
            place_name: selectedLocation.place_name,
            place_id: selectedLocation.place_id,
            currentPlaceName,
            extractedLocation,
            previousLastFetched,
            previousRequestId,
            newRequestId,
            wasFetching,
            timestamp: new Date().toISOString(),
          }
        );
        // Clear previous data immediately when location changes
        console.log(
          '🟢 [PlaceDetailsContainer] Clearing previous location data...',
          {
            previousLocation: previousLastFetched,
            newLocation: currentPlaceName,
            timestamp: new Date().toISOString(),
          }
        );
        clearOverViewData();
        clearImages();
        clearQuickPeekData();

        // Clear image cache to prevent showing same images for different locations
        ThumbnailImageProvider.clearCache();

        console.log(
          ' [PlaceDetailsContainer]  Previous location data cleared',
          {
            newRequestId,
            timestamp: new Date().toISOString(),
          }
        );
      } else {
        // Same location - don't reset refs, don't clear data
        // This prevents duplicate API calls when React re-renders
        console.log(
          '🟢 [PlaceDetailsContainer] ⏭️ SKIPPING ref reset (same location):',
          {
            currentPlaceName,
            extractedLocation,
            lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
            currentRequestId: currentRequestIdRef.current,
            currentLocationNameRef: currentLocationNameRef.current,
            isFetching: wasFetching,
            locationMatches:
              extractedLocation === lastFetchedPlaceNameRef.current,
            timestamp: new Date().toISOString(),
          }
        );
      }
    }
    // Only depend on selectedLocation and extractedLocation
    // extractedLocation is derived from selectedLocation, so it will update when selectedLocation changes
    // The clear functions are stable from Zustand stores, so they won't cause re-renders
    // selectedLocation properties are accessed but not needed as dependencies since we want
    // the effect to run on object reference change, not individual property changes
  }, [
    selectedLocation,
    extractedLocation,
    clearOverViewData,
    clearImages,
    clearQuickPeekData,
  ]);

  // Fetch overview when we have location and session
  // Always call API when place name changes, regardless of place_id or coordinates
  useEffect(() => {
    console.log('🔴 [PlaceDetailsContainer] API fetch effect triggered:', {
      extractedLocation,
      sessionId,
      userId,
      lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
      isFetching: isFetchingRef.current,
      currentRequestId: currentRequestIdRef.current,
      selectedLocationTitle: selectedLocation?.title,
      selectedLocationPlace: selectedLocation?.place,
      selectedLocationPlaceName: selectedLocation?.place_name,
      locationMatches: extractedLocation === lastFetchedPlaceNameRef.current,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
    });

    // Use extractedLocation (place name) as the trigger
    // This ensures every click on a different place name triggers a new API call
    if (!extractedLocation) {
      console.log(
        '🔴 [PlaceDetailsContainer] ❌ No extractedLocation, skipping API call'
      );
      return;
    }

    if (!sessionId) {
      console.log(
        '🔴 [PlaceDetailsContainer] ❌ No sessionId, skipping API call'
      );
      return;
    }

    if (!userId) {
      console.log('🔴 [PlaceDetailsContainer] ❌ No userId, skipping API call');
      return;
    }

    // Check if we need to fetch (ref was reset by the selectedLocation change effect)
    // Also check if we're already fetching to prevent duplicate calls
    // Only check location name - if name is different, treat as different location (name takes priority)
    const currentPlaceId = selectedLocation?.place_id || '';
    const isLocationDifferent =
      extractedLocation !== lastFetchedPlaceNameRef.current;
    const shouldFetch =
      isLocationDifferent && !isFetchingRef.current && extractedLocation !== '';

    console.log('🔴 [PlaceDetailsContainer] Fetch decision:', {
      extractedLocation,
      lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
      currentPlaceId,
      lastFetchedPlaceId: lastFetchedPlaceIdRef.current,
      isFetching: isFetchingRef.current,
      locationMatches: extractedLocation === lastFetchedPlaceNameRef.current,
      shouldFetch,
      timestamp: new Date().toISOString(),
    });

    if (shouldFetch) {
      const overviewRequest = {
        user_id: String(userId ?? '').trim(),
        session_id: String(sessionId ?? '').trim(),
        location: String(extractedLocation ?? '').trim(),
        place_id: selectedLocation?.place_id ?? '',
        lat: selectedLocation?.latitude,
        lng: selectedLocation?.longitude,
      };
      const quickPeekRequest = {
        user_id: String(userId ?? '').trim(),
        session_id: String(sessionId ?? '').trim(),
        location: String(extractedLocation ?? '').trim(),
        place_id: selectedLocation?.place_id ?? '',
      };
      const cachedOverview = getCachedOverview(overviewRequest);
      const cachedQuickPeek = getCachedQuickPeek(quickPeekRequest);

      // Apply cached overview to store so Overview tab shows instantly on reopen
      if (cachedOverview?.overview) {
        const ov = cachedOverview.overview;
        const hasValid =
          (ov.name &&
            ov.name.trim() !== '' &&
            ov.name !== 'Unknown Location') ||
          (ov.description && ov.description.trim() !== '') ||
          (Array.isArray(ov.pointers) && ov.pointers.length > 0) ||
          (Array.isArray(ov.imageUrls) && ov.imageUrls.length > 0);
        if (hasValid) {
          const overviewName =
            ov.name && ov.name.trim() !== '' && ov.name !== 'Unknown Location'
              ? ov.name
              : extractedLocation || '';
          addOverview({
            name: overviewName,
            description: ov.description || '',
            district: ov.district || '',
            city: ov.city || '',
            state: ov.state || '',
            pincode: ov.pincode || '',
            rating: ov.rating || '',
            reviews: ov.reviews || '',
            type: ov.type || '',
            imageUrls: ov.imageUrls || [],
            pointers: ov.pointers || [],
          });
        }
        if (cachedOverview.festivals?.length) {
          cachedOverview.festivals.forEach(festival => {
            if (
              festival?.title &&
              !String(festival.title).toLowerCase().includes('placeholder')
            ) {
              addFestival({
                title: festival.title,
                description: festival.description,
                longDescription: festival.longDescription,
                imageUrls: festival.imageUrls,
              });
            }
          });
        }
        if (cachedOverview.placesOfInterest?.length) {
          cachedOverview.placesOfInterest.forEach(place => {
            if (
              place?.title &&
              !String(place.title).toLowerCase().includes('placeholder')
            ) {
              addPlaceOfInterest({
                title: place.title,
                description: place.description,
                longDescription: place.longDescription,
                imageUrls: place.imageUrls,
              });
            }
          });
        }
      }

      // Apply cached quick peek so Travel Guide tab shows instantly on reopen
      if (cachedQuickPeek) {
        setQuickPeekResponse(cachedQuickPeek);

        // Transform bestTimeToVisit from string to object if needed
        if (
          cachedQuickPeek.bestTimeToVisit &&
          typeof cachedQuickPeek.bestTimeToVisit === 'string'
        ) {
          const { setBestTimeToVisit } = useQuickPeekStore.getState();
          setBestTimeToVisit({
            title: 'Best Time To Visit',
            durations: [
              {
                month: cachedQuickPeek.bestTimeToVisit,
                preferredTravelMode: '',
                costBudget: '',
              },
            ],
            preferredTravelMode: '',
            icon: '',
          });
        }

        setLoadingTravelPlanningOnly(false);
        setLoadingNavigationAccessibility(false);
        setLoadingCulturalSocial(false);
        setLoadingHistoricalSouvenirs(false);
        setLoadingActivitiesFacilities(false);
      }

      // If both overview and quick peek are cached, skip network entirely
      if (cachedOverview && cachedQuickPeek) {
        lastFetchedPlaceNameRef.current = extractedLocation;
        lastFetchedPlaceIdRef.current = currentPlaceId;
        isFetchingRef.current = false;
        return;
      }

      // Set the refs before making API calls to prevent duplicate calls
      lastFetchedPlaceNameRef.current = extractedLocation;
      lastFetchedPlaceIdRef.current = currentPlaceId;
      isFetchingRef.current = true;

      const requestId = currentRequestIdRef.current;
      const requestLocationName = extractedLocation;
      const signal = abortControllerRef.current?.signal;

      // Fetch overview only if not served from cache
      const overviewPromise = (
        !cachedOverview
          ? fetchOverviewDataRef.current(
              {
                user_id: userId,
                session_id: sessionId,
                location: extractedLocation,
                place_id: selectedLocation?.place_id || '',
                lat: selectedLocation?.latitude,
                lng: selectedLocation?.longitude,
              },
              signal
            )
          : Promise.resolve(null)
      )
        .then(response => {
          console.log(
            '🔴 [PlaceDetailsContainer] ✅ Overview API response received:',
            {
              hasResponse: !!response,
              hasOverview: !!response?.overview,
              overviewName: response?.overview?.name,
              requestId,
              currentRequestId: currentRequestIdRef.current,
              requestLocationName,
              timestamp: new Date().toISOString(),
            }
          );

          // Check if this request is still valid (user hasn't clicked on a different place)
          // Compare request IDs - if they don't match, this is a stale request
          // CRITICAL: Double-check here to ensure we don't process stale responses
          const isStaleRequest = currentRequestIdRef.current !== requestId;

          // CRITICAL: Also check if the location name still matches
          // This prevents stale responses from being accepted even if requestId matches
          // (which can happen if the component remounts and resets the ref)
          // Use ref to get the current location name (not closure value which might be stale)
          const currentLocationName = currentLocationNameRef.current;
          const locationMatches = currentLocationName === requestLocationName;

          console.log(
            '🔴 [PlaceDetailsContainer] 🔍 Location validation check:',
            {
              requestId,
              currentRequestId: currentRequestIdRef.current,
              requestLocationName,
              currentLocationName,
              locationMatches,
              isStaleRequest,
              willReject: isStaleRequest || !locationMatches,
              currentLocationNameFromRef: currentLocationNameRef.current,
              timestamp: new Date().toISOString(),
            }
          );

          if (isStaleRequest || !locationMatches) {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Overview response ignored (stale request):',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                requestLocationName,
                currentLocationName,
                locationMatches,
                areEqual: currentRequestIdRef.current === requestId,
                difference: currentRequestIdRef.current - requestId,
                timestamp: new Date().toISOString(),
              }
            );
            return null; // Ignore stale response - return null to prevent any processing
          }

          // CRITICAL: Double-check again right before updating store to prevent race conditions
          // This ensures that even if the requestId or location changed between the first check and now,
          // we still won't update the store with stale data
          // Use ref to get the current location name (not closure value which might be stale)
          const currentLocationNameForValidation =
            currentLocationNameRef.current;
          const isStillValid =
            currentRequestIdRef.current === requestId &&
            currentLocationNameForValidation === requestLocationName;
          if (!isStillValid) {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Overview response became stale before store update:',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                requestLocationName,
                currentLocationName: currentLocationNameForValidation,
                locationMatches:
                  currentLocationNameForValidation === requestLocationName,
                timestamp: new Date().toISOString(),
              }
            );
            return null; // Ignore stale response
          }

          console.log(
            '🔴 [PlaceDetailsContainer] ✅ Overview response is valid:',
            {
              requestId,
              currentRequestId: currentRequestIdRef.current,
              timestamp: new Date().toISOString(),
            }
          );

          if (response) {
            // Helper to check if overview has valid data
            // Check for name, description, pointers, or images - any of these means we have valid data
            const hasName =
              response.overview?.name &&
              typeof response.overview.name === 'string' &&
              response.overview.name.trim() !== '' &&
              response.overview.name !== 'Unknown Location';
            const hasDescription =
              response.overview?.description &&
              typeof response.overview.description === 'string' &&
              response.overview.description.trim() !== '' &&
              response.overview.description.trim() !==
                'This is a placeholder for a location whose details are not known.';
            const hasPointers =
              Array.isArray(response.overview?.pointers) &&
              response.overview.pointers.length > 0 &&
              response.overview.pointers.some(
                pointer =>
                  Array.isArray(pointer.items) && pointer.items.length > 0
              );
            const hasImages =
              Array.isArray(response.overview?.imageUrls) &&
              response.overview.imageUrls.length > 0;

            const hasValidOverview =
              hasName || hasDescription || hasPointers || hasImages;

            // Update overview store if we have any valid data
            if (response.overview && hasValidOverview) {
              // Use extractedLocation as name if name is "Unknown Location" or empty
              const overviewName =
                response.overview.name &&
                response.overview.name.trim() !== '' &&
                response.overview.name !== 'Unknown Location'
                  ? response.overview.name
                  : extractedLocation || '';

              console.log(
                '🔴 [PlaceDetailsContainer] 📝 Updating overview store with valid data:',
                {
                  requestId,
                  overviewName,
                  hasDescription: !!response.overview.description,
                  hasPointers: hasPointers,
                  hasImages: hasImages,
                  timestamp: new Date().toISOString(),
                }
              );

              addOverview({
                name: overviewName,
                description: response.overview.description || '',
                district: response.overview.district || '',
                city: response.overview.city || '',
                state: response.overview.state || '',
                pincode: response.overview.pincode || '',
                rating: response.overview.rating || '',
                reviews: response.overview.reviews || '',
                type: response.overview.type || '',
                imageUrls: response.overview.imageUrls || [],
                pointers: response.overview.pointers || [],
              });

              console.log(
                '🔴 [PlaceDetailsContainer] ✅ Overview store updated',
                {
                  requestId,
                  overviewName,
                  timestamp: new Date().toISOString(),
                }
              );
            } else {
              console.log(
                '🔴 [PlaceDetailsContainer] ⚠️ Overview response has no valid data:',
                {
                  requestId,
                  hasOverview: !!response.overview,
                  hasValidOverview,
                  hasName,
                  hasDescription,
                  hasPointers,
                  hasImages,
                  timestamp: new Date().toISOString(),
                }
              );
            }

            // Add festivals (already filtered by transformer, but double-check)
            if (response.festivals && response.festivals.length > 0) {
              console.log('🔴 [PlaceDetailsContainer] Adding festivals:', {
                requestId,
                festivalCount: response.festivals.length,
                timestamp: new Date().toISOString(),
              });
              response.festivals.forEach(festival => {
                // Only add if not placeholder
                if (
                  festival.title &&
                  !festival.title.toLowerCase().includes('placeholder')
                ) {
                  addFestival({
                    title: festival.title,
                    description: festival.description,
                    longDescription: festival.longDescription,
                    imageUrls: festival.imageUrls,
                  });

                  // Fetch images for festival if it doesn't have imageUrls
                  if (
                    (!festival.imageUrls || festival.imageUrls.length === 0) &&
                    festival.title
                  ) {
                    // Use Unsplash API to fetch festival image
                    imageService
                      .fetchImage(festival.title, {
                        category: 'Festivals & Celebrations',
                      })
                      .then(imageUrl => {
                        if (imageUrl) {
                          updateFestivalImages(festival.title, [imageUrl]);
                        }
                      })
                      .catch(err => {
                        console.error(
                          `Error fetching Unsplash image for festival ${festival.title}:`,
                          err
                        );
                      });
                  }
                }
              });
            }

            // Add places of interest (already filtered by transformer, but double-check)
            if (
              response.placesOfInterest &&
              response.placesOfInterest.length > 0
            ) {
              console.log(
                '🔴 [PlaceDetailsContainer] Adding places of interest:',
                {
                  requestId,
                  placesCount: response.placesOfInterest.length,
                  timestamp: new Date().toISOString(),
                }
              );
              response.placesOfInterest.forEach(place => {
                // Only add if not placeholder
                if (
                  place.title &&
                  !place.title.toLowerCase().includes('placeholder')
                ) {
                  addPlaceOfInterest({
                    title: place.title,
                    description: place.description,
                    longDescription: place.longDescription,
                    imageUrls: place.imageUrls,
                  });
                }
              });
            }
          }
        })
        .catch(err => {
          console.error(
            '🔴 [PlaceDetailsContainer] ❌ Error fetching overview:',
            {
              error: err,
              requestId,
              currentRequestId: currentRequestIdRef.current,
              extractedLocation,
              timestamp: new Date().toISOString(),
            }
          );
          // Only reset refs if this was the current request (not a stale one)
          if (currentRequestIdRef.current === requestId) {
            lastFetchedPlaceNameRef.current = '';
            isFetchingRef.current = false;
            console.log(
              '🔴 [PlaceDetailsContainer] Refs reset after overview error (current request):',
              {
                requestId,
                lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
                isFetching: isFetchingRef.current,
                timestamp: new Date().toISOString(),
              }
            );
          } else {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Error from stale request, not resetting refs:',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                timestamp: new Date().toISOString(),
              }
            );
          }
        });

      // Helper function to validate and merge partial response into store
      const validateAndMergePartialResponse = (
        response: Partial<QuickPeekResponse> | null,
        apiName: string
      ): boolean => {
        // Validate request is still current
        if (currentRequestIdRef.current !== requestId) {
          console.log(
            `🔴 [PlaceDetailsContainer] ❌ ${apiName} API response rejected - stale request ID`,
            {
              requestId,
              currentRequestId: currentRequestIdRef.current,
              requestLocationName,
              currentLocationName: currentLocationNameRef.current,
              timestamp: new Date().toISOString(),
            }
          );
          return false;
        }

        // Validate location name still matches
        const currentLocationName = currentLocationNameRef.current;
        const locationMatches = currentLocationName === requestLocationName;
        if (!locationMatches) {
          console.log(
            `🔴 [PlaceDetailsContainer] ❌ ${apiName} API response rejected - location name mismatch`,
            {
              requestLocationName,
              currentLocationName,
              requestId,
              currentRequestId: currentRequestIdRef.current,
              timestamp: new Date().toISOString(),
            }
          );
          return false;
        }

        if (!response) {
          console.log(
            `🔴 [PlaceDetailsContainer] ❌ ${apiName} API response rejected - no response data`,
            {
              requestId,
              currentRequestId: currentRequestIdRef.current,
              requestLocationName,
              timestamp: new Date().toISOString(),
            }
          );
          return false;
        }

        // Transform bestTimeToVisit from string to object if needed
        if (
          response.bestTimeToVisit &&
          typeof response.bestTimeToVisit === 'string'
        ) {
          const { setBestTimeToVisit } = useQuickPeekStore.getState();
          setBestTimeToVisit({
            title: 'Best Time To Visit',
            durations: [
              {
                month: response.bestTimeToVisit,
                preferredTravelMode: '',
                costBudget: '',
              },
            ],
            preferredTravelMode: '',
            icon: '',
          });
        }

        // Get current store state and merge with new partial response
        // Use deep merge to preserve all data from previous API calls
        const currentResponse = useQuickPeekStore.getState().quickPeekResponse;

        // Deep merge function to combine nested objects and arrays
        const deepMerge = (
          target: Partial<QuickPeekResponse>,
          source: Partial<QuickPeekResponse>
        ): QuickPeekResponse => {
          const result = { ...target } as QuickPeekResponse;

          for (const key in source) {
            if (source.hasOwnProperty(key)) {
              const sourceValue = source[key];
              const targetValue = result[key];

              // If both are arrays, merge them (avoid duplicates)
              if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
                // Create a Set to avoid duplicates based on string representation
                const uniqueItems = new Set([
                  ...targetValue.map(item => JSON.stringify(item)),
                  ...sourceValue.map(item => JSON.stringify(item)),
                ]);
                // Convert back to array, preserving order
                result[key] = Array.from(uniqueItems).map(item =>
                  JSON.parse(item)
                ) as unknown;
              }
              // If both are objects (but not arrays), deep merge them
              else if (
                sourceValue &&
                typeof sourceValue === 'object' &&
                !Array.isArray(sourceValue) &&
                targetValue &&
                typeof targetValue === 'object' &&
                !Array.isArray(targetValue)
              ) {
                result[key] = deepMerge(
                  targetValue as Partial<QuickPeekResponse>,
                  sourceValue as Partial<QuickPeekResponse>
                ) as unknown;
              }
              // Otherwise, use source value (or keep target if source is undefined/null)
              else if (sourceValue !== undefined && sourceValue !== null) {
                result[key] = sourceValue as unknown;
              }
            }
          }

          return result;
        };

        const mergedResponse = deepMerge(currentResponse || {}, response || {});

        console.log(
          `💾 [PlaceDetailsContainer] 🚀 MERGING ${apiName} into store:`,
          {
            requestId,
            apiName,
            responseKeys: response ? Object.keys(response) : [],
            currentResponseKeys: currentResponse
              ? Object.keys(currentResponse)
              : [],
            mergedKeys: Object.keys(mergedResponse),
            hasTravelModes: !!mergedResponse.travelModes,
            travelModesCount: mergedResponse.travelModes?.length || 0,
            hasCulturalAspects: !!mergedResponse.culturalAspects,
            hasActivities: !!mergedResponse.activities,
            timestamp: new Date().toISOString(),
          }
        );

        setQuickPeekResponse(mergedResponse);

        // Cache the complete merged response for future instant retrieval
        setCachedQuickPeek(quickPeekRequest, mergedResponse);

        return true;
      };

      // Use same quickPeekRequest for fetches so cache keys match on write (hook uses this request for setQueryData)
      const quickPeekRequestBody = quickPeekRequest;

      // Call all 5 QuickPeek APIs only if not served from cache
      if (!cachedQuickPeek) {
        setLoadingTravelPlanningOnly(true);
        setLoadingNavigationAccessibility(true);
        setLoadingCulturalSocial(true);
        setLoadingHistoricalSouvenirs(true);
        setLoadingActivitiesFacilities(true);
      }

      const travelPlanningParallelPromise = !cachedQuickPeek
        ? fetchTravelPlanningParallelRef
            .current(quickPeekRequestBody, signal)
            .then(({ travelPlanningOnly, navigationAccessibility }) => {
              console.log(
                '🔴 [PlaceDetailsContainer] ✅ Travel Planning Parallel API responses received:',
                {
                  hasTravelPlanningOnly: !!travelPlanningOnly,
                  hasNavigationAccessibility: !!navigationAccessibility,
                  requestId,
                  currentRequestId: currentRequestIdRef.current,
                  timestamp: new Date().toISOString(),
                }
              );

              // Process travel-planning-only first (display in order)
              // Handle success or failure independently
              if (travelPlanningOnly) {
                if (
                  validateAndMergePartialResponse(
                    travelPlanningOnly,
                    'Travel Planning Only'
                  )
                ) {
                  setTimeout(() => {
                    setLoadingTravelPlanningOnly(false);
                  }, 0);
                } else {
                  setLoadingTravelPlanningOnly(false);
                }
              } else {
                // API failed or returned no data - stop loading but don't block other API
                console.warn(
                  '⚠️ [PlaceDetailsContainer] Travel Planning Only API returned no data'
                );
                setLoadingTravelPlanningOnly(false);
              }

              // Process navigation-accessibility second (display in order)
              // Handle success or failure independently
              if (navigationAccessibility) {
                if (
                  validateAndMergePartialResponse(
                    navigationAccessibility,
                    'Navigation Accessibility'
                  )
                ) {
                  setTimeout(() => {
                    setLoadingNavigationAccessibility(false);
                  }, 0);
                } else {
                  setLoadingNavigationAccessibility(false);
                }
              } else {
                // API failed or returned no data - stop loading but don't block other API
                console.warn(
                  '⚠️ [PlaceDetailsContainer] Navigation Accessibility API returned no data'
                );
                setLoadingNavigationAccessibility(false);
              }

              // Merge both responses for backward compatibility
              // Even if one failed, merge what we have
              const mergedResponse = {
                ...(travelPlanningOnly || {}),
                ...(navigationAccessibility || {}),
              };

              return mergedResponse;
            })
            .catch(err => {
              // This catch should rarely be hit since we use Promise.allSettled
              // But handle it gracefully just in case
              console.error(
                '🔴 [PlaceDetailsContainer] ❌ Unexpected error in Travel Planning parallel fetch:',
                {
                  error: err,
                  requestId,
                  timestamp: new Date().toISOString(),
                }
              );
              // Set both to false to prevent infinite loading
              setLoadingTravelPlanningOnly(false);
              setLoadingNavigationAccessibility(false);
              return null;
            })
        : Promise.resolve(null);

      const travelPlanningPromise = travelPlanningParallelPromise;

      const culturalParallelPromise = !cachedQuickPeek
        ? fetchCulturalParallelRef
            .current(quickPeekRequestBody, signal)
            .then(({ culturalSocial, historicalSouvenirs }) => {
              console.log(
                '🔴 [PlaceDetailsContainer] ✅ Cultural Parallel API responses received:',
                {
                  hasCulturalSocial: !!culturalSocial,
                  hasHistoricalSouvenirs: !!historicalSouvenirs,
                  requestId,
                  currentRequestId: currentRequestIdRef.current,
                  timestamp: new Date().toISOString(),
                }
              );

              // Process cultural-social first (display in order)
              // Handle success or failure independently
              if (culturalSocial) {
                if (
                  validateAndMergePartialResponse(
                    culturalSocial,
                    'Cultural Social'
                  )
                ) {
                  setTimeout(() => {
                    setLoadingCulturalSocial(false);
                  }, 0);
                } else {
                  setLoadingCulturalSocial(false);
                }
              } else {
                // API failed or returned no data - stop loading but don't block other API
                console.warn(
                  '⚠️ [PlaceDetailsContainer] Cultural Social API returned no data'
                );
                setLoadingCulturalSocial(false);
              }

              // Process historical-souvenirs second (display in order)
              // Handle success or failure independently
              if (historicalSouvenirs) {
                if (
                  validateAndMergePartialResponse(
                    historicalSouvenirs,
                    'Historical Souvenirs'
                  )
                ) {
                  setTimeout(() => {
                    setLoadingHistoricalSouvenirs(false);
                  }, 0);
                } else {
                  setLoadingHistoricalSouvenirs(false);
                }
              } else {
                // API failed or returned no data - stop loading but don't block other API
                console.warn(
                  '⚠️ [PlaceDetailsContainer] Historical Souvenirs API returned no data'
                );
                setLoadingHistoricalSouvenirs(false);
              }

              // Merge both responses for backward compatibility
              // Even if one failed, merge what we have
              const mergedResponse = {
                ...(culturalSocial || {}),
                ...(historicalSouvenirs || {}),
              };

              return mergedResponse;
            })
            .catch(err => {
              // This catch should rarely be hit since we use Promise.allSettled
              // But handle it gracefully just in case
              console.error(
                '🔴 [PlaceDetailsContainer] ❌ Unexpected error in Cultural parallel fetch:',
                {
                  error: err,
                  requestId,
                  timestamp: new Date().toISOString(),
                }
              );
              // Set both to false to prevent infinite loading
              setLoadingCulturalSocial(false);
              setLoadingHistoricalSouvenirs(false);
              return null;
            })
        : Promise.resolve(null);

      const culturalInfoPromise = culturalParallelPromise;

      const activitiesFacilitiesPromise = !cachedQuickPeek
        ? fetchActivitiesFacilitiesRef
            .current(quickPeekRequestBody, signal)
            .then(response => {
              console.log(
                '🔴 [PlaceDetailsContainer] ✅ Activities & Facilities API response received:',
                {
                  hasResponse: !!response,
                  requestId,
                  currentRequestId: currentRequestIdRef.current,
                  timestamp: new Date().toISOString(),
                }
              );
              // Display immediately when response arrives
              if (
                validateAndMergePartialResponse(
                  response,
                  'Activities & Facilities'
                )
              ) {
                setTimeout(() => {
                  setLoadingActivitiesFacilities(false);
                }, 0);
              } else {
                setLoadingActivitiesFacilities(false);
              }
              return response;
            })
            .catch(err => {
              console.error(
                '🔴 [PlaceDetailsContainer] ❌ Error fetching Activities & Facilities:',
                {
                  error: err,
                  requestId,
                  timestamp: new Date().toISOString(),
                }
              );
              setLoadingActivitiesFacilities(false);
              return null;
            })
        : Promise.resolve(null);

      // Wait for all promises to complete
      Promise.allSettled([
        travelPlanningPromise,
        culturalInfoPromise,
        activitiesFacilitiesPromise,
      ])
        .then(([travelResult, culturalResult, activitiesResult]) => {
          const isCurrentRequest = currentRequestIdRef.current === requestId;

          console.log(
            '🔴 [PlaceDetailsContainer] ✅ All 5 QuickPeek APIs completed in parallel:',
            {
              requestId,
              currentRequestId: currentRequestIdRef.current,
              travelResult: travelResult.status,
              culturalResult: culturalResult.status,
              activitiesResult: activitiesResult.status,
              isCurrentRequest,
              timestamp: new Date().toISOString(),
            }
          );

          if (!isCurrentRequest) {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Results from stale request, ignoring:',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                difference: currentRequestIdRef.current - requestId,
                timestamp: new Date().toISOString(),
              }
            );
          }
        })
        .catch(err => {
          console.error(
            '🔴 [PlaceDetailsContainer] ❌ Error in parallel API calls:',
            {
              error: err,
              requestId,
              currentRequestId: currentRequestIdRef.current,
              timestamp: new Date().toISOString(),
            }
          );
        })
        .finally(() => {
          // Only reset isFetching if this was the current request
          if (currentRequestIdRef.current === requestId) {
            console.log(
              '🔴 [PlaceDetailsContainer] Resetting isFetching (current request):',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                timestamp: new Date().toISOString(),
              }
            );
            isFetchingRef.current = false;

            // Cache the complete merged response for future instant retrieval
            const currentResponse =
              useQuickPeekStore.getState().quickPeekResponse;
            if (currentResponse) {
              setCachedQuickPeek(quickPeekRequest, currentResponse);
              console.log(
                '🔴 [PlaceDetailsContainer] ✅ Cached complete merged response:',
                {
                  requestId,
                  responseKeys: Object.keys(currentResponse),
                  timestamp: new Date().toISOString(),
                }
              );
            }
          } else {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Not resetting isFetching (stale request):',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                timestamp: new Date().toISOString(),
              }
            );
          }
        });

      // Reset fetching flag when overview promise completes (overview runs in parallel)
      Promise.allSettled([overviewPromise])
        .then(([overviewResult]) => {
          // CRITICAL: Check if this is still the current request before processing results
          // This prevents processing results from stale requests
          const isCurrentRequest = currentRequestIdRef.current === requestId;

          console.log('🔴 [PlaceDetailsContainer] ✅ Overview API completed:', {
            requestId,
            currentRequestId: currentRequestIdRef.current,
            overviewResult: overviewResult.status,
            isCurrentRequest,
            timestamp: new Date().toISOString(),
          });

          if (!isCurrentRequest) {
            console.log(
              '🔴 [PlaceDetailsContainer] ⚠️ Overview result from stale request, ignoring:',
              {
                requestId,
                currentRequestId: currentRequestIdRef.current,
                difference: currentRequestIdRef.current - requestId,
                timestamp: new Date().toISOString(),
              }
            );
          }
        })
        .catch(err => {
          console.error(
            '🔴 [PlaceDetailsContainer] ❌ Error in overview promise:',
            {
              error: err,
              requestId,
              currentRequestId: currentRequestIdRef.current,
              timestamp: new Date().toISOString(),
            }
          );
        });
    } else {
      console.log(
        '🔴 [PlaceDetailsContainer] ❌ SKIPPING API CALLS (already fetched or fetching):',
        {
          extractedLocation,
          lastFetchedPlaceName: lastFetchedPlaceNameRef.current,
          isFetching: isFetchingRef.current,
          locationMatches:
            extractedLocation === lastFetchedPlaceNameRef.current,
          timestamp: new Date().toISOString(),
        }
      );
    }
    // isOverviewLoading and isQuickPeekLoading intentionally excluded -
    // we want to allow new API calls even if previous ones are still loading
    // selectedLocation properties are intentionally excluded - we only use them inside the effect,
    // not to trigger it. extractedLocation already depends on selectedLocation.
    // fetchOverviewData and fetchQuickPeekData are stored in refs to prevent
    // duplicate calls when these functions are recreated (they should be stable from React Query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    extractedLocation,
    sessionId,
    userId,
    addOverview,
    addFestival,
    addPlaceOfInterest,
    updateFestivalImages,
    addInfoPointer,
    addModeOfTransport,
    addTravelPlanning,
    setCachedQuickPeek, // Add this to dependency array
    selectedLocation?.latitude, // Added for linting
    selectedLocation?.longitude, // Added for linting
    selectedLocation?.place_id, // Added for linting
  ]);

  // Determine which location to display
  // Only show if a location is explicitly selected (clicked)
  const displayLocation = useMemo(() => {
    // Only show if a location is explicitly selected
    return selectedLocation || null;
  }, [selectedLocation]);

  // Determine place name
  const placeName = useMemo(() => {
    if (displayLocation) {
      return (
        displayLocation.title ||
        displayLocation.place ||
        displayLocation.place_name ||
        displayLocation.locality ||
        'Unknown Location'
      );
    }
    return '';
  }, [displayLocation]);

  // Don't render if we don't have an explicitly selected location
  if (!displayLocation) {
    return null;
  }

  // Prepare props for PlaceDetailsPresenter
  console.log(
    '📤 [PlaceDetailsContainer] Preparing props for PlaceDetailsPresenter:',
    {
      placeName,
      isOverviewLoading,
      isQuickPeekLoading: isQuickPeekLoadingCombined,
      hasDisplayLocation: !!displayLocation,
      timestamp: new Date().toISOString(),
    }
  );

  const presenterProps: PlaceDetailsPresenterProps = {
    placeName,
    location: displayLocation || undefined,
    onBack: handleBack,
    isOverviewLoading,
    isQuickPeekLoading: isQuickPeekLoadingCombined,
    sessionId,
    userId,
  };

  return (
    <div
      className='scrollbar-hide'
      style={{
        width: '100%',
        maxWidth: '100%',
        height: '100%', // Use 100% to respect parent padding
        borderRadius: 'var(--Dimensions-Border-border-double-large, 16px)',
        border: '1px solid var(--Colors-Stroke-Base, #D6D3D1)',
        background: 'var(--Colors-Background-primary, #FFF)',
        boxShadow: '4px 4px 16px 0 rgba(36, 27, 4, 0.10)',
        display: 'flex',
        padding:
          'var(--Dimensions-Spacing-1, 4px) var(--Dimensions-Spacing-3, 12px)',
        alignItems: 'flex-start',
        gap: '8px',
        flex: '1 0 0',
        boxSizing: 'border-box',
        overflowY: 'auto', // Vertical scroll on container
        overflowX: 'hidden', // Prevent horizontal scroll
        position: 'relative',
        paddingBottom: '40px', // Add bottom padding to prevent content from overlapping border
      }}
    >
      <PlaceDetailsPresenter {...presenterProps} />
    </div>
  );
};

// Utility functions to help find locations from messages
export const findLocationByCardId = (
  messages: ChatMessage[],
  cardId: string
): ChatLocation | null => {
  // FIXED: Search through ALL messages (from newest to oldest), not just the latest
  // This ensures we can find places from previous chat conversations
  const messagesWithLocations = messages
    .slice()
    .reverse()
    .filter(
      msg =>
        msg.role === 'assistant' && msg.locations && msg.locations.length > 0
    );

  if (messagesWithLocations.length === 0) {
    return null;
  }

  // Card IDs can be in format: place_id_index (e.g., "ChIJxxx_0")
  // Extract the place_id part (everything before the last underscore and number)
  // Also handle cases where cardId is just the place_id
  let placeIdToMatch = cardId;

  // Check if cardId has the format "place_id_index"
  const underscoreIndex = cardId.lastIndexOf('_');
  if (underscoreIndex > 0) {
    const afterUnderscore = cardId.substring(underscoreIndex + 1);
    // If the part after underscore is a number, it's likely an index
    if (/^\d+$/.test(afterUnderscore)) {
      placeIdToMatch = cardId.substring(0, underscoreIndex);
    }
  }

  // Search through all messages with locations
  for (const message of messagesWithLocations) {
    if (message.locations) {
      const foundLocation = message.locations.find(
        location =>
          location.place_id === placeIdToMatch ||
          location.place_id === cardId ||
          location.title === cardId ||
          location.place === cardId
      );

      if (foundLocation) {
        return foundLocation;
      }
    }
  }

  return null;
};

export const findLocationByPlaceName = (
  messages: ChatMessage[],
  placeName: string
): ChatLocation | null => {
  // Search through all messages (from newest to oldest) to find the location
  // This ensures we can find places from any message in the conversation
  const messagesWithLocations = messages
    .slice()
    .reverse()
    .filter(
      msg =>
        msg.role === 'assistant' && msg.locations && msg.locations.length > 0
    );

  const normalizedPlaceName = placeName.toLowerCase().trim();

  // Search through all messages with locations
  for (const message of messagesWithLocations) {
    if (message.locations) {
      // First, try to find exact match on place_name (highest priority)
      // This ensures we get the correct location when multiple locations share the same place_id
      let foundLocation = message.locations.find(
        loc => loc.place_name?.toLowerCase() === normalizedPlaceName
      );

      if (foundLocation) {
        return foundLocation;
      }

      // Second, try exact matches on other fields
      foundLocation = message.locations.find(
        loc =>
          loc.title?.toLowerCase() === normalizedPlaceName ||
          loc.place?.toLowerCase() === normalizedPlaceName ||
          loc.locality?.toLowerCase() === normalizedPlaceName
      );

      if (foundLocation) {
        return foundLocation;
      }

      // Finally, try partial matches (contains)
      foundLocation = message.locations.find(
        loc =>
          loc.place_name?.toLowerCase().includes(normalizedPlaceName) ||
          loc.title?.toLowerCase().includes(normalizedPlaceName) ||
          loc.place?.toLowerCase().includes(normalizedPlaceName) ||
          loc.locality?.toLowerCase().includes(normalizedPlaceName)
      );

      if (foundLocation) {
        return foundLocation;
      }
    }
  }

  return null;
};
